/* ============================================================
   Plano e limites

   Mora no core pelo mesmo motivo que a pontuação: os dois lados
   precisam da MESMA resposta. A tela diz "restam 3 estimativas hoje"
   antes de pedir, e o servidor recusa a quarta sem confiar no cliente.
   Duas cópias desse número divergiriam, e a pessoa veria uma promessa
   que o servidor não cumpre.

   Os limites moram num documento no banco (`config/app`) para poderem
   mudar sem publicar o app. O que está aqui é o PADRÃO e o formato — o
   que vale quando o documento não existe, ou quando alguém salvou lixo
   nele.
   ============================================================ */

import type { DayKey } from "./types.js";
import { DOLAR_PADRAO, MODELO_PADRAO, modeloConhecido } from "./ia.js";
import { FUSO_PADRAO } from "./lembrete.js";

export type Plano = "gratis" | "assinante";

export interface AppConfig {
  /** Estimativas de caloria por dia, para quem não assina. */
  tetoCaloriaGratis: number;
  /** E para quem assina. Alto, não infinito: laço com bug não é uso. */
  tetoCaloriaAssinante: number;
  /** Desafios ATIVOS ao mesmo tempo, para quem não assina. */
  desafiosGratis: number;

  /* ---- IA: o que roda e quanto custa ----

     O modelo mora aqui, e não numa constante da função, porque trocar de
     modelo é decisão de custo — quem decide olha o painel de gasto, não
     o código. E porque um modelo que começa a errar precisa ser trocado
     no minuto seguinte, não no deploy seguinte. */
  modeloIA: string;
  /** Câmbio para converter o custo. Estimativa, e a que envelhece mais rápido. */
  dolar: number;

  /* ---- assinatura ----

     O preço de verdade mora no Play Console; este é o número que o
     painel usa para estimar receita. Os dois podem divergir, e é por
     isso que a tela diz "estimada" em vez de "receita". */
  precoMensalCentavos: number;
}

/**
 * O padrão.
 *
 * `tetoCaloriaGratis` começa folgado de propósito. O teto de hoje é 25
 * para todo mundo; cair direto para 2 seria tirar o que as pessoas já
 * usam, e limite se aperta com dado na mão, não com chute.
 */
export const CONFIG_PADRAO: AppConfig = {
  tetoCaloriaGratis: 5,
  tetoCaloriaAssinante: 100,
  desafiosGratis: 2,
  modeloIA: MODELO_PADRAO,
  dolar: DOLAR_PADRAO,
  precoMensalCentavos: 0,
};

const inteiro = (v: unknown, padrao: number, minimo = 0): number => {
  const n = Math.trunc(Number(v));
  return Number.isFinite(n) && n >= minimo ? n : padrao;
};

/** Mesma desconfiança do `inteiro`, para o que tem casa decimal. */
const positivo = (v: unknown, padrao: number): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : padrao;
};

/**
 * Lê o documento de configuração com desconfiança.
 *
 * Campo faltando ou com lixo cai no padrão em vez de virar `NaN` — e
 * `NaN >= teto` é falso, o que transformaria uma configuração errada em
 * "ninguém mais estima caloria" sem nenhum erro aparecer.
 */
export function resolverAppConfig(bruto: unknown): AppConfig {
  const d = (bruto ?? {}) as Record<string, unknown>;
  return {
    tetoCaloriaGratis: inteiro(d["tetoCaloriaGratis"], CONFIG_PADRAO.tetoCaloriaGratis),
    tetoCaloriaAssinante: inteiro(d["tetoCaloriaAssinante"], CONFIG_PADRAO.tetoCaloriaAssinante),
    desafiosGratis: inteiro(d["desafiosGratis"], CONFIG_PADRAO.desafiosGratis, 1),
    /* Modelo fora do catálogo cai no padrão. Um id com erro de digitação
       faria a API recusar TODA estimativa, e o sintoma chegaria como "a
       IA parou" sem ninguém ligar uma coisa à outra. */
    modeloIA: modeloConhecido(d["modeloIA"])
      ? String(d["modeloIA"]) : CONFIG_PADRAO.modeloIA,
    dolar: positivo(d["dolar"], CONFIG_PADRAO.dolar),
    precoMensalCentavos: inteiro(
      d["precoMensalCentavos"], CONFIG_PADRAO.precoMensalCentavos),
  };
}

/** Só "assinante" é assinante. Qualquer outra coisa é grátis. */
export const ehAssinante = (plano: unknown): boolean => plano === "assinante";

export const tetoDeCaloria = (config: AppConfig, plano: unknown): number =>
  ehAssinante(plano) ? config.tetoCaloriaAssinante : config.tetoCaloriaGratis;

/**
 * Quantas estimativas ainda cabem hoje.
 *
 * Mora aqui e não numa conta solta na tela porque agora DUAS telas
 * perguntam: a de publicar, pra decidir se o interruptor de estimar
 * junto pode ficar ligado, e a do prato, pra decidir entre o botão e o
 * recado de cota. Duas subtrações iguais escritas em dois lugares é
 * exatamente como uma delas fica pra trás no dia em que o limite muda.
 *
 * Nunca negativo: o contador do servidor pode passar o teto (o teto caiu
 * no painel depois de a pessoa já ter usado), e "restam -2 estimativas"
 * na tela é pior que "acabaram".
 */
export const restamEstimativas = (
  config: AppConfig, plano: unknown, usadasHoje: number,
): number => {
  const usadas = Number.isFinite(usadasHoje) ? Math.max(0, Math.trunc(usadasHoje)) : 0;
  return Math.max(0, tetoDeCaloria(config, plano) - usadas);
};

/* ---------- quando a cota volta ----------

   "Amanhã tem mais" não ajuda quem está com o celular na mão às onze da
   noite, e nem quem está de madrugada. O contador do servidor vive em
   `users/{uid}/usage/{dia}`, e o `dia` é o de SÃO PAULO — não o do
   aparelho. Quem estiver viajando lê o fuso errado e a tela promete uma
   hora que não é a do servidor.

   Por isso o cálculo sai daqui, do mesmo lugar onde mora o teto, e usa o
   mesmo fuso que a função usa pra montar a chave do documento. */

/**
 * Quantos minutos faltam pro contador de estimativas zerar.
 *
 * Nunca zero: na virada exata, "faltam 0 minutos" é a frase mais
 * confusa possível numa tela — o mínimo é um.
 */
export function minutosAteACotaZerar(agora: Date = new Date()): number {
  const partes = new Intl.DateTimeFormat("en-GB", {
    timeZone: FUSO_PADRAO,
    hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(agora);
  const pega = (tipo: string) =>
    Number(partes.find((p) => p.type === tipo)?.value ?? 0);

  const passados = pega("hour") * 3600 + pega("minute") * 60 + pega("second");
  return Math.max(1, Math.ceil((86400 - passados) / 60));
}

/**
 * O mesmo, em palavras: "4h37", "37 min", "1 min".
 *
 * Sem segundos de propósito. O número é pra decidir se vale esperar ou
 * assinar, e nessa decisão a diferença entre 4h37 e 4h37m12s não existe
 * — mas um relógio correndo na tela pede um `setInterval` e uma
 * re-renderização por segundo numa tela que não precisa de nenhum dos
 * dois.
 */
export function faltaPraCotaZerar(agora: Date = new Date()): string {
  const minutos = minutosAteACotaZerar(agora);
  if (minutos < 60) return `${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
}

/** Assinante não tem limite de desafios; `Infinity` diz isso sem if na tela. */
export const limiteDeDesafios = (config: AppConfig, plano: unknown): number =>
  ehAssinante(plano) ? Infinity : config.desafiosGratis;

/**
 * O desafio ainda está rolando?
 *
 * O limite conta desafios ATIVOS, não os que a pessoa já entrou na vida.
 * Contar tudo faria o teto ser atingido em dois meses e nunca mais
 * liberar — vira paywall permanente em vez de limite de uso. Contando só
 * os que não acabaram, o limite respira sozinho.
 *
 * Um desafio sem `endDay` conta como ativo: ausência de fim não é fim.
 */
/**
 * Até quando o desafio vale, para quem precisa de uma data.
 *
 * DATA FINAL É OPCIONAL: um grupo que combina "vamos postar todo dia" não
 * tem fim, e obrigar uma data punha um vencedor artificial numa coisa que
 * não acaba. Vazio significa "por enquanto não acaba".
 *
 * Quem compara intervalo precisa de uma data mesmo assim, e é aqui que
 * ela sai. **Não use `?? "2999-12-31"`**: o `??` só cai em `null` e
 * `undefined`, e o campo vazio chega como `""` — a comparação vira
 * `"2026-09-03" <= ""`, que é falsa, e o desafio some da tela sem erro
 * nenhum. Foi assim em três lugares.
 */
export const fimEfetivo = (endDay: string | null | undefined): string =>
  endDay || "2999-12-31";

export const desafioAtivo = (
  desafio: { endDay?: string | null }, hoje: DayKey,
): boolean => !desafio.endDay || desafio.endDay >= hoje;

export const quantosAtivos = (
  desafios: readonly { endDay?: string | null }[], hoje: DayKey,
): number => desafios.filter((d) => desafioAtivo(d, hoje)).length;

/**
 * Cabe mais um?
 *
 * Vale tanto para criar quanto para entrar por convite — o limite é de
 * PARTICIPAÇÃO, como no GymRats. Quem chega no teto escolhe: sair de um
 * que acabou de valer, esperar um terminar, ou assinar.
 */
export function cabeMaisUmDesafio(
  config: AppConfig, plano: unknown,
  desafios: readonly { endDay?: string | null }[], hoje: DayKey,
): boolean {
  return quantosAtivos(desafios, hoje) < limiteDeDesafios(config, plano);
}
